<template>
    <div class="filter-min-max">
        <div class="filter-label" @click="showOptions = !showOptions">
            {{ valueLabel }}
        </div>
        <div
            class="filter-options"
            v-if="showOptions"
            v-click-outside="clickedOutsideOptions"
            :style="{}"
        >
            <div class="fields">
                <div class="field">
                    <label>{{ t('reportFilters-min') }}:</label>
                    <date-range-picker
                        ref="picker"
                        opens="center"
                        :locale-data="dateLocale"
                        :maxDate="today"
                        :singleDatePicker="true"
                        :autoApply="true"
                        :ranges="false"
                        v-model="min"
                    />
                    <span
                        class="far fa-remove clear-date"
                        v-if="minDate"
                        @click.stop.prevent="min.startDate = null"
                    ></span>
                </div>
                <div class="field">
                    <label>{{ t('reportFilters-max') }}:</label>
                    <date-range-picker
                        ref="picker"
                        opens="center"
                        :locale-data="dateLocale"
                        :maxDate="today"
                        :minDate="minDate"
                        :singleDatePicker="true"
                        :autoApply="true"
                        :ranges="false"
                        v-model="max"
                    />
                    <span
                        class="far fa-remove clear-date"
                        v-if="maxDate"
                        @click.stop.prevent="max.startDate = null"
                    ></span>
                </div>
            </div>
            <div class="filter-actions">
                <button
                    type="button"
                    class="cancelBtn btn btn-sm btn-default"
                    @click="cancel"
                >
                    {{ t('reportFilters-close') }}
                </button>
            </div>
        </div>
    </div>
</template>
<style lang="scss" scoped>
.field {
    display: flex;
    position: relative;
    label {
        width: auto;
        display: block;
        margin-right: 10px;
    }
    .clear-date {
        padding-right: 0;
        position: absolute;
        right: 3px;
        top: 11px;
        margin-top: 2px;
        font-size: 12px;
        color: #333;
        background: transparent;
        border: none;
        padding-left: 0;
    }
}
::v-deep {
    .form-control {
        border: none;
        border-bottom: 1px solid #ddd;
        border-radius: 0;
        box-shadow: none;
        font-size: 13px;
        flex-grow: 1;
        width: 100%;
        min-width: 130px;
        margin-top: 6px;
        &:focus {
            outline: none;
            box-shadow: none;
            border: none;
            border-bottom: 1px solid #ddd;
        }
    }
}
</style>
<script>
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";

export default {
    name: "min-max-dates",
    props: {
        options: {
            type: Object,
            default: () => ({
                mask: "R$ ######,##",
                optionsWidth: 300,
            }),
        },
        name: {
            type: String,
            default: "min-max-dates",
        },
        currentValue: {
            type: Object,
            default: () => ({
                min: "",
                max: "",
            }),
        },
    },
    components: { DateRangePicker },
    data() {
        return {
            showOptions: false,
            clickedOutsideOptions: () => {},
            value: "",
            today: new Date(),
            changedValue: false,
            min: {
                startDate: "",
                endDate: "",
            },
            max: {
                startDate: "",
                endDate: "",
            },
        };
    },
    mounted() {
        if (this.currentValue) {
            let value = this.currentValue;
            this.max.startDate = value.max;
            this.min.startDate = value.min;
        }
        if (this.value.length > 0) {
            this.changed();
        }
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        cancel() {
            this.showOptions = false;
        },
        changed() {
            this.$emit("changed", this.value);
        },
        setValue() {
            let min = this.minDate
                ? new Date(this.min.startDate.toISOString())
                : "";
            let max = this.maxDate
                ? new Date(this.max.startDate.toISOString())
                : "";
            if (min) {
                min.setHours(0);
                min.setMinutes(0);
                min.setSeconds(0);
                min.setMilliseconds(0);
            }
            if (max) {
                max.setHours(23);
                max.setMinutes(59);
                max.setSeconds(59);
                max.setMilliseconds(999);
            }
            this.value = {
                min: min ? min.toISOString() : "",
                max: max ? max.toISOString() : "",
            };
        },
        formatDate(date) {
            const d = new Date(date);
            if (d) {
                return (
                    d.getDate() +
                    "/" +
                    (d.getMonth() + 1) +
                    "/" +
                    d.getFullYear()
                );
            }
            return "";
            return new Date(date).toLocaleDateString("pt-BR");
        },
    },
    computed: {
        minDate() {
            return this.min.startDate;
        },
        maxDate() {
            return this.max.startDate;
        },
        maxFloat() {
            return parseFloat(
                String(this.max)
                    .replace(/[^0-9,]/gi, "")
                    .replace(",", "."),
            );
        },
        minFloat() {
            return parseFloat(
                String(this.min)
                    .replace(/[^0-9,]/gi, "")
                    .replace(",", "."),
            );
        },
        dateLocale() {
            return {
                firstDay: 1,
                format: 'dd-mm-yyyy',
                daysOfWeek: this.t('reportFilters-date_picker-days_of_week'),
                monthNames: this.t('reportFilters-date_picker-months'),
                applyLabel: this.t('reportFilters-apply'),
                cancelLabel: this.t('reportFilters-cancel'),
            };
        },
        valueLabel() {
            if (!this.min.startDate && !this.max.startDate) {
                return this.t('reportFilters-enter_dates');
            } else if (!this.minDate && this.maxDate) {
                return this.t('reportFilters-up_to', [this.formatDate(this.maxDate)]);
            } else if (!this.maxDate && this.minDate) {
                return this.t('reportFilters-after', [this.formatDate(this.minDate)]);
            } else {
                return this.t('reportFilters-between', [this.formatDate(this.minDate), this.formatDate(this.maxDate)]);
            }
            return "";
        },
    },
    watch: {
        showOptions() {
            this.setValue();
            if (this.showOptions) {
                window.setTimeout(() => {
                    this.clickedOutsideOptions = () => {
                        if (this.showOptions) {
                            this.cancel();
                        }
                    };
                }, 100);
            } else {
                this.clickedOutsideOptions = () => {};
            }
        },
        value() {
            this.changed();
        },
    },
};
</script>
